import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FaCircleCheck, FaCircleMinus, FaEnvelope, FaHourglassHalf, FaRegCircle } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Box, Flex } from "../../../../styled-system/jsx";
import Card from "../../../components/core/basic/Card";
import type { PersonStatus } from "../model";
import { INK, STATUS_COLORS } from "../theme";
import { useActivityLabels } from "../useActivityLabels";

export function Section({
  title,
  description,
  aside,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card css={{ display: "flex", flexDirection: "column", gap: "medium", padding: "large", borderRadius: "standard" }}>
      <Flex gap="medium" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
        <Box>
          <h2 className={css({ margin: 0, color: "text.heading", fontSize: "xlarge", fontWeight: "600" })}>{title}</h2>
          {description && (
            <p className={css({ margin: 0, marginTop: "xsmall", color: "text.secondary", fontSize: "medium" })}>
              {description}
            </p>
          )}
        </Box>
        {aside}
      </Flex>
      {children}
    </Card>
  );
}

const STATUS_ICONS: Record<PersonStatus, ReactNode> = {
  active: <FaCircleCheck />,
  slowing: <FaHourglassHalf />,
  inactive: <FaCircleMinus />,
  never: <FaRegCircle />,
  invited: <FaEnvelope />,
};

/** Status always travels as icon + label; the color only reinforces it. */
export function StatusBadge({ status }: { status: PersonStatus }) {
  const labels = useActivityLabels();
  return (
    <span
      title={labels.statusHint(status)}
      className={css({
        display: "inline-flex",
        gap: "6px",
        alignItems: "center",
        borderRadius: "pill",
        paddingX: "10px",
        paddingY: "3px",
        color: "darkgrey",
        fontSize: "small",
        fontWeight: "600",
        whiteSpace: "nowrap",
        backgroundColor: "#f4f2f7",
      })}
    >
      <span style={{ color: STATUS_COLORS[status], display: "inline-flex" }} aria-hidden>
        {STATUS_ICONS[status]}
      </span>
      {labels.status(status)}
    </span>
  );
}

export function Pill({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "brand" }) {
  return (
    <span
      className={css({
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "pill",
        paddingX: "8px",
        paddingY: "1px",
        fontSize: "xsmall",
        fontWeight: "700",
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      })}
      style={
        tone === "brand" ?
          { color: "#9b0c49", backgroundColor: "#fde7f0" }
        : { color: INK.secondary, backgroundColor: "#eeecf2" }
      }
    >
      {children}
    </span>
  );
}

/** A plotless trend line: no axis or hover, so a tile or table row can carry many without the weight of a chart. */
export function Sparkline({
  values,
  color,
  width = 120,
  height = 32,
  label,
}: {
  values: number[];
  color: string;
  width?: number;
  height?: number;
  label: string;
}) {
  const path = useMemo(() => {
    if (values.length < 2) return null;
    const max = Math.max(...values, 1);
    const pad = 3;
    const step = (width - pad * 2) / (values.length - 1);
    const points = values.map((value, index) => [
      pad + index * step,
      height - pad - (value / max) * (height - pad * 2),
    ]);
    const line = points.map(([x, y], index) => `${index ? "L" : "M"}${x!.toFixed(1)},${y!.toFixed(1)}`).join("");
    const area = `${line}L${points.at(-1)![0]!.toFixed(1)},${height - pad}L${pad},${height - pad}Z`;
    return { line, area, last: points.at(-1)! };
  }, [values, width, height]);

  if (!path) return null;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path d={path.area} fill={color} opacity={0.12} />
      <path d={path.line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={path.last[0]} cy={path.last[1]} r={3} fill={color} stroke="white" strokeWidth={2} />
    </svg>
  );
}

const DAY_SECONDS = 24 * 60 * 60;
// [unit, its length, the age from which it is used]. Days run to two months: "47 days ago" is more useful to
// someone deciding whom to chase than "2 months ago".
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number, number][] = [
  ["year", 365 * DAY_SECONDS, 365 * DAY_SECONDS],
  ["month", 30 * DAY_SECONDS, 60 * DAY_SECONDS],
  ["day", DAY_SECONDS, DAY_SECONDS],
  ["hour", 60 * 60, 60 * 60],
  ["minute", 60, 60],
];

export function useRelativeTime() {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const formatter = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    return (date: Date, now: Date = new Date()) => {
      const seconds = (date.getTime() - now.getTime()) / 1000;
      for (const [unit, size, from] of RELATIVE_UNITS) {
        if (Math.abs(seconds) >= from) return formatter.format(Math.trunc(seconds / size), unit);
      }
      return formatter.format(0, "minute");
    };
  }, [i18n.language]);
}

export function useCount() {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const whole = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 });
    const one = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
    const percent = new Intl.NumberFormat(i18n.language, { style: "percent", maximumFractionDigits: 0 });
    return {
      whole: (value: number) => whole.format(value),
      oneDecimal: (value: number) => one.format(value),
      percent: (value: number) => percent.format(value),
    };
  }, [i18n.language]);
}
